import { useParams, useSelectedLayoutSegments } from "next/navigation";
import type {
  Dispatch,
  MutableRefObject,
  ReactNode,
  SetStateAction,
} from "react";
import { useCallback, useMemo } from "react";
import {
  createContext,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { useLocalStorage } from "usehooks-ts";

import { Ducky } from "@/components/app/ducky/ducky";
import { useBackgroundPathname } from "@/hooks/use-background-pathname";
import { useIsClient } from "@/hooks/use-is-client";
import { useMobile } from "@/hooks/use-mobile";
import { APP_ROUTES } from "@/utils/constants";
import { isPWA } from "@/utils/helpers";
import { LocalStorageKeys } from "@/utils/local-storage-keys";
import { stripUnreadCountPrefix } from "@/utils/page-title";
import { matchRoute } from "@/utils/route-utils";
import { getVisualKeyboardInsetDebugInfo } from "@/utils/visual-keyboard-inset";

const DEFAULT_MEDIA_VOLUME = 1;
const VISUAL_KEYBOARD_INSET_VAR = "--visual-keyboard-inset-bottom";
const VISUAL_KEYBOARD_INSET_HALF_VAR = "--visual-keyboard-inset-bottom-half";
const VISUAL_KEYBOARD_OFFSET_TOP_VAR = "--visual-keyboard-offset-top";
const VISUAL_KEYBOARD_OFFSET_TOP_HALF_VAR = "--visual-keyboard-offset-top-half";

const defaultRouteConfig = {
  hideTopNav: false,
  hideMobileNav: false,
  rightSidebar: true,
  discoveryWidget: true,
  coinInfoWidget: false,
  floatingPostButton: false,
  pullToRefresh: true,
};

const getRouteConfig = ({
  pathname,
  layoutSegments,
  params,
}: {
  pathname: string;
  layoutSegments: string[];
  params: Record<string, string | string[] | undefined>;
}) => {
  switch (true) {
    case matchRoute(pathname, APP_ROUTES.app):
      return { ...defaultRouteConfig, floatingPostButton: true };
    case matchRoute(pathname, APP_ROUTES.settings):
    case matchRoute(pathname, APP_ROUTES.admin):
    case matchRoute(pathname, APP_ROUTES.trade):
    case matchRoute(pathname, APP_ROUTES.wallet):
    case matchRoute(pathname, APP_ROUTES.messages):
      return {
        ...defaultRouteConfig,
        rightSidebar: false,
        pullToRefresh: false,
      };
    case matchRoute(pathname, APP_ROUTES.collections):
      return {
        ...defaultRouteConfig,
        // Only the main `/collections/publicKey` page hides the right sidebar
        rightSidebar: pathname.split("/").filter(Boolean).length > 2,
      };
    // Profile page
    case params.userId &&
      layoutSegments[0] === "(app)" &&
      layoutSegments[1] === params.userId:
      return {
        ...defaultRouteConfig,
        coinInfoWidget: true,
        discoveryWidget: false,
        floatingPostButton: true,
      };
    default:
      return defaultRouteConfig;
  }
};

type LayoutContextType = typeof defaultRouteConfig & {
  hideTopNav: boolean;
  hideMobileNav: boolean;
  setHideMobileNav: (isHidden: boolean) => void;
  setHideTopNav: (isHidden: boolean) => void;
  setDynamicTitle: (fn: (originalTitle: string) => string) => void;
  safeInsetTop: number;
  pwa: boolean;
  ios: boolean;
  enableDucky: boolean;
  setEnableDucky: Dispatch<SetStateAction<boolean>>;
  setPlayingElement: Dispatch<
    SetStateAction<null | HTMLAudioElement | HTMLVideoElement>
  >;
  mediaVolume: number;
  setMediaVolume: Dispatch<SetStateAction<number>>;
  mediaMuted: boolean;
  setMediaMuted: Dispatch<SetStateAction<boolean>>;
  videoAutoplayEnabled: boolean;
  setVideoAutoplayEnabled: Dispatch<SetStateAction<boolean>>;
  history: string[];
  playingElement: HTMLAudioElement | HTMLVideoElement | null;
  prevPlayingElementRef: MutableRefObject<
    HTMLAudioElement | HTMLVideoElement | null
  >;
  firstPostPopup: boolean;
  setFirstPostPopup: Dispatch<SetStateAction<boolean>>;
};

const LayoutContext = createContext<LayoutContextType>({
  ...defaultRouteConfig,
  hideTopNav: false,
  hideMobileNav: false,
  setHideMobileNav: () => {},
  setHideTopNav: () => {},
  setDynamicTitle: () => {},
  safeInsetTop: 0,
  pwa: false,
  ios: false,
  enableDucky: false,
  setEnableDucky: () => {},
  setPlayingElement: () => {},
  mediaVolume: DEFAULT_MEDIA_VOLUME,
  setMediaVolume: () => {},
  mediaMuted: true,
  setMediaMuted: () => {},
  videoAutoplayEnabled: true,
  setVideoAutoplayEnabled: () => {},
  history: [],
  playingElement: null,
  prevPlayingElementRef: { current: null },
  firstPostPopup: false,
  setFirstPostPopup: () => {},
});

export const LayoutProvider = ({ children }: { children: ReactNode }) => {
  const layoutSegments = useSelectedLayoutSegments();
  const params = useParams();

  const pathname = useBackgroundPathname();
  const routeConfig = getRouteConfig({ pathname, layoutSegments, params });
  const [enableDucky, setEnableDucky] = useLocalStorage("enableDucky", false);
  const [playingElement, setPlayingElement] = useState<
    HTMLAudioElement | HTMLVideoElement | null
  >(null);
  const prevPlayingElementRef = useRef<typeof playingElement>(null);
  const [mediaVolume, setMediaVolume] = useLocalStorage(
    "mediaVolume",
    DEFAULT_MEDIA_VOLUME,
  );
  const [mediaMuted, setMediaMuted] = useState(true);
  const [videoAutoplayEnabled, setVideoAutoplayEnabled] = useLocalStorage(
    LocalStorageKeys.videoAutoplayEnabled,
    true,
  );

  const [firstPostPopup, setFirstPostPopup] = useState(false);
  const [hideTopNav, setHideTopNav] = useState(routeConfig.hideTopNav);
  const [hideMobileNav, setHideMobileNav] = useState(routeConfig.hideMobileNav);
  const originalTitle = useRef(
    typeof document === "undefined"
      ? ""
      : stripUnreadCountPrefix(document.title),
  );
  const activeDynamicTitleIdRef = useRef(0);
  const isClient = useIsClient();
  const { hasMouse } = useMobile();
  const historyRef = useRef<string[]>([]);

  const pwa = useMemo(() => isClient && isPWA(), [isClient]);
  const ios = useMemo(
    () => isClient && document.documentElement.classList.contains("os-ios"),
    [isClient],
  );
  const safeInsetTop = useMemo(
    () =>
      isClient &&
      typeof document !== "undefined" &&
      isPWA() &&
      document.documentElement.classList.contains("os-ios")
        ? 64
        : 0,
    [isClient],
  );

  useLayoutEffect(() => {
    originalTitle.current = stripUnreadCountPrefix(document.title);
    activeDynamicTitleIdRef.current = 0;
    historyRef.current.push(pathname);
  }, [pathname]);

  useEffect(() => {
    if (!ios) {
      return;
    }

    const visualViewport = window.visualViewport;

    if (!visualViewport) {
      return;
    }

    const getKeyboardInsetDebugInfo = () =>
      getVisualKeyboardInsetDebugInfo({
        activeElement: document.activeElement,
        layoutViewportHeight: window.innerHeight,
        visualViewportHeight: visualViewport.height,
        visualViewportOffsetTop: visualViewport.offsetTop,
      });

    const updateKeyboardInset = () => {
      const debugInfo = getKeyboardInsetDebugInfo();
      const keyboardInset = debugInfo.keyboardInset;
      const keyboardInsetHalf = Math.round(keyboardInset / 2);
      const keyboardOffsetTop =
        keyboardInset > 0
          ? Math.max(0, Math.round(visualViewport.offsetTop))
          : 0;
      const keyboardOffsetTopHalf = Math.round(keyboardOffsetTop / 2);

      document.body.style.setProperty(
        VISUAL_KEYBOARD_INSET_VAR,
        `${keyboardInset}px`,
      );
      document.body.style.setProperty(
        VISUAL_KEYBOARD_INSET_HALF_VAR,
        `${keyboardInsetHalf}px`,
      );
      document.body.style.setProperty(
        VISUAL_KEYBOARD_OFFSET_TOP_VAR,
        `${keyboardOffsetTop}px`,
      );
      document.body.style.setProperty(
        VISUAL_KEYBOARD_OFFSET_TOP_HALF_VAR,
        `${keyboardOffsetTopHalf}px`,
      );
      document.documentElement.style.setProperty(
        VISUAL_KEYBOARD_INSET_VAR,
        `${keyboardInset}px`,
      );
      document.documentElement.style.setProperty(
        VISUAL_KEYBOARD_INSET_HALF_VAR,
        `${keyboardInsetHalf}px`,
      );
      document.documentElement.style.setProperty(
        VISUAL_KEYBOARD_OFFSET_TOP_VAR,
        `${keyboardOffsetTop}px`,
      );
      document.documentElement.style.setProperty(
        VISUAL_KEYBOARD_OFFSET_TOP_HALF_VAR,
        `${keyboardOffsetTopHalf}px`,
      );
    };

    const pendingKeyboardUpdateTimeouts = new Set<number>();
    let pendingKeyboardUpdateFrame: number | null = null;

    const cancelScheduledKeyboardInsetUpdates = () => {
      if (pendingKeyboardUpdateFrame !== null) {
        window.cancelAnimationFrame(pendingKeyboardUpdateFrame);
        pendingKeyboardUpdateFrame = null;
      }

      pendingKeyboardUpdateTimeouts.forEach((timeoutId) => {
        window.clearTimeout(timeoutId);
      });
      pendingKeyboardUpdateTimeouts.clear();
    };

    const scheduleKeyboardInsetUpdate = () => {
      cancelScheduledKeyboardInsetUpdates();
      updateKeyboardInset();

      pendingKeyboardUpdateFrame = window.requestAnimationFrame(() => {
        pendingKeyboardUpdateFrame = null;
        updateKeyboardInset();
      });

      [80, 300].forEach((delayMs) => {
        const timeoutId = window.setTimeout(() => {
          pendingKeyboardUpdateTimeouts.delete(timeoutId);
          updateKeyboardInset();
        }, delayMs);

        pendingKeyboardUpdateTimeouts.add(timeoutId);
      });
    };

    const handleVisualViewportResize = () => updateKeyboardInset();
    const handleVisualViewportScroll = () => updateKeyboardInset();
    const handleWindowResize = () => scheduleKeyboardInsetUpdate();
    const handleOrientationChange = () => scheduleKeyboardInsetUpdate();
    const handleFocusIn = () => scheduleKeyboardInsetUpdate();
    const handleFocusOut = () => scheduleKeyboardInsetUpdate();

    updateKeyboardInset();
    visualViewport.addEventListener("resize", handleVisualViewportResize);
    visualViewport.addEventListener("scroll", handleVisualViewportScroll);
    window.addEventListener("resize", handleWindowResize);
    window.addEventListener("orientationchange", handleOrientationChange);
    document.addEventListener("focusin", handleFocusIn);
    document.addEventListener("focusout", handleFocusOut);

    return () => {
      cancelScheduledKeyboardInsetUpdates();
      visualViewport.removeEventListener("resize", handleVisualViewportResize);
      visualViewport.removeEventListener("scroll", handleVisualViewportScroll);
      window.removeEventListener("resize", handleWindowResize);
      window.removeEventListener("orientationchange", handleOrientationChange);
      document.removeEventListener("focusin", handleFocusIn);
      document.removeEventListener("focusout", handleFocusOut);
      document.body.style.removeProperty(VISUAL_KEYBOARD_INSET_VAR);
      document.body.style.removeProperty(VISUAL_KEYBOARD_INSET_HALF_VAR);
      document.body.style.removeProperty(VISUAL_KEYBOARD_OFFSET_TOP_VAR);
      document.body.style.removeProperty(VISUAL_KEYBOARD_OFFSET_TOP_HALF_VAR);
      document.documentElement.style.removeProperty(VISUAL_KEYBOARD_INSET_VAR);
      document.documentElement.style.removeProperty(
        VISUAL_KEYBOARD_INSET_HALF_VAR,
      );
      document.documentElement.style.removeProperty(
        VISUAL_KEYBOARD_OFFSET_TOP_VAR,
      );
      document.documentElement.style.removeProperty(
        VISUAL_KEYBOARD_OFFSET_TOP_HALF_VAR,
      );
    };
  }, [ios]);

  const setDynamicTitle = useCallback(
    (fn: (originalTitle: string) => string): (() => void) => {
      const cleanOriginalTitle = stripUnreadCountPrefix(
        originalTitle.current || document.title,
      );
      const dynamicTitleId = activeDynamicTitleIdRef.current + 1;

      originalTitle.current = cleanOriginalTitle;
      activeDynamicTitleIdRef.current = dynamicTitleId;
      document.title = fn(cleanOriginalTitle);

      return () => {
        if (activeDynamicTitleIdRef.current !== dynamicTitleId) {
          return;
        }

        activeDynamicTitleIdRef.current = 0;
        document.title = cleanOriginalTitle;
      };
    },
    [],
  );

  useEffect(() => {
    if (prevPlayingElementRef.current) {
      prevPlayingElementRef.current.pause();
    }
    prevPlayingElementRef.current = playingElement;
  }, [playingElement]);

  return (
    <LayoutContext.Provider
      value={{
        ...routeConfig,
        hideTopNav,
        hideMobileNav,
        setHideTopNav,
        setHideMobileNav,
        setDynamicTitle,
        safeInsetTop,
        pwa,
        ios,
        enableDucky,
        setEnableDucky,
        setPlayingElement,
        playingElement,
        prevPlayingElementRef,
        // Only want volume slider if the user has mouse
        // Otherwise we treat the user as a device user in which case
        // volume is handled by the OS.
        mediaVolume: hasMouse ? mediaVolume : 1,
        setMediaVolume,
        mediaMuted,
        setMediaMuted,
        videoAutoplayEnabled,
        setVideoAutoplayEnabled,
        history: historyRef.current,
        firstPostPopup,
        setFirstPostPopup,
      }}
    >
      {children}
      {enableDucky && <Ducky />}
    </LayoutContext.Provider>
  );
};

export const useLayout = () => {
  const context = useContext(LayoutContext);
  if (!context) {
    throw new Error("useLayout must be used within a LayoutProvider");
  }
  return context;
};
